'use client';

import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PERMISSIONS, usePerm } from '@/hooks/use-perm';
import { isGuidedSetupEnabled, isQuickStartHubEnabled } from '@/lib/setupFeatures';
import { useQuickStartSummary } from '@/lib/api/setup';
import { useStoresList } from '@/lib/api/stores';

const RemainingBadge = ({ count }: { count: number }) => (
  <span className="bg-primary/15 text-primary ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums">
    {count}
  </span>
);

const NoStoreAlert = ({ label }: { label: string }) => (
  <Tooltip delayDuration={300}>
    <TooltipTrigger asChild>
      <span className="relative ml-auto inline-flex size-[22px] shrink-0 items-center justify-center">
        <span
          className="crm-sidebar-store-alert-dot crm-sidebar-store-alert-dot--setup crm-sidebar-quickstart-alert-dot"
          aria-label={label}
        />
      </span>
    </TooltipTrigger>
    <TooltipContent
      side="right"
      className="max-w-[220px] border border-amber-200/90 bg-amber-50 text-xs text-amber-950 shadow-sm dark:border-amber-800 dark:bg-amber-950 dark:text-amber-50"
      arrowClassName="bg-amber-50 fill-amber-50 dark:bg-amber-950 dark:fill-amber-950"
    >
      {label}
    </TooltipContent>
  </Tooltip>
);

/**
 * Runs the actual store lookup + summary fetch. Mounted only when the user has
 * STORES_VIEW so `stores.list` never fires for users lacking that permission.
 */
function QuickStartNavBadgeInner() {
  const { t } = useTranslation('navigation');
  const { data: storesData } = useStoresList({
    pagination: { page: 1, pageSize: 1 },
  });
  const hasStores = (storesData?.stores?.length ?? 0) > 0;
  const { data } = useQuickStartSummary({ enabled: hasStores && isQuickStartHubEnabled() });

  if (!hasStores) return <NoStoreAlert label={t('quickStartNoStore')} />;

  const remaining = data?.progress.remainingRequired ?? 0;
  if (!isQuickStartHubEnabled() || remaining <= 0) return null;

  return <RemainingBadge count={remaining} />;
}

export function QuickStartNavBadge() {
  const { can, isLoaded } = usePerm();

  if (!isGuidedSetupEnabled()) return null;
  if (!isLoaded) return null;
  if (!can(PERMISSIONS.STORES_VIEW)) return null;

  return <QuickStartNavBadgeInner />;
}
